import React, { useState, useEffect } from 'react'
import { Form, Input, Button, message } from 'antd'
import { LeftOutlined } from '@ant-design/icons'
import { validNewPsd } from '@/utils/validator'
import './index.scss'
import { PasswordLevelVerificationRules, passwordSecurityLevel, PwdValidStatus, PwdValidStatusDescription } from '@/config/default'
import intl from 'react-intl-universal'
import user from '@/utils/user'
import login from '@/utils/login'
import PasswordStrength from '@/components/passwordStrength'
import { updatePsdApi } from '@/services/login'
import { Encrypt } from '@/utils/aes'

type PasswordLevel = typeof passwordSecurityLevel[keyof typeof passwordSecurityLevel]
type PwdValidStatusType = typeof PwdValidStatus[keyof typeof PwdValidStatus]

interface ValidationOption {
  id: number
  label: string
  iconType?: string
  validate: (value: string) => boolean
}

interface UpdatePsdProps {
  passwordLevel?: PasswordLevel
  pwdValidStatus?: PwdValidStatusType
  onCancel?: () => void
  passwordLengthMin?: number
  passwordLengthMax?: number
}

function UpdatePsd (props: UpdatePsdProps) {
  const {
    passwordLevel = passwordSecurityLevel.LOW,
    pwdValidStatus = PwdValidStatus.NORMAL,
    onCancel,
    passwordLengthMin,
    passwordLengthMax
  } = props
  const [form] = Form.useForm()
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [confirmDirty, setConfirmDirty] = useState(false)
  const [validateGroup, setValidateGroup] = useState<((value: string) => boolean)[]>([])
  const [options, setOptions] = useState<ValidationOption[]>([])
  const [newPsd, setNewPsd] = useState(null)
  const [passwordFocused, setPasswordFocused] = useState(false)

  useEffect(() => {
    renderPasswordLevelVerification()
  }, [])

  // 一些清理工作
  const handleCancel = () => {
    setConfirmLoading(false)
    setNewPsd(null)
    form.resetFields()
    renderPasswordLevelVerification()
    onCancel && onCancel()
  }

  const onOk = async () => {
    try {
      setConfirmLoading(true)
      const values = await form.validateFields()
      await updatePsdApi({
        newPassword: Encrypt(values.newPsd),
        oldPassword: Encrypt(values.oldPsd)
      })
      message.success(intl.get('login.passwordChanged'))
      user.removeStagingUserInfo()
      user.remove()
      login.logout()
      onCancel && onCancel()
    } catch (error) {
      console.log(error)
    } finally {
      setConfirmLoading(false)
    }
  }

  const compareToFirstPassword = (rule, value, callback) => {
    if (value && value !== form.getFieldValue('newPsd')) {
      callback(new Error(intl.get('login.psdsNotMatch')))
    } else {
      callback()
    }
  }

  const validateToNextPassword = (rule, value, callback) => {
    const userData = user.get() || user.getStagingUserInfo() || { enterprise: {} }
    const min = userData.enterprise.passwordLengthMin
    const max = userData.enterprise.passwordLengthMax
    if (value) {
      // 密码校验正则表达式组校验
      for (let index = 0; index < validateGroup.length; index++) {
        if (!validateGroup[index](value)) {
          return callback(new Error(options[index].label))
        }
      }
      // validateGroup.forEach((validate, index) => {
      //   if (!validate(value)) {
      //     return callback(new Error(options[index].label))
      //   }
      // })
      if (!validNewPsd(value, passwordLevel, min, max)) {
        return callback(new Error(intl.get('login.psdNotLegal2')))
      }
    }
    callback()
  }

  const handlePasswordInput = e => {
    const { value } = e.target
    // 密码校验正则表达式组校验-图标状态控制
    const updatedOptions = [...options]
    validateGroup.forEach((validate, index) => {
      updatedOptions[index] = { ...updatedOptions[index], iconType: validate(value) ? 'check-circle' : 'info-circle' }
    })
    setNewPsd(value)
    setOptions(updatedOptions)
    // 用户已输入过确认密码时，新密码变更后重新校验确认密码
    if (confirmDirty) {
      form.validateFields(['rePsd'])
    }
  }

  const handleConfirmBlur = e => {
    const { value } = e.target
    setConfirmDirty(prev => prev || !!value)
  }

  // 根据密码安全等级获取密码安全等级对应的密码校验规则组配置
  const renderPasswordLevelVerification = () => {
    const currentPasswordLevel = passwordLevel
    const opts = currentPasswordLevel && PasswordLevelVerificationRules[currentPasswordLevel]
    const userData = user.get() || user.getStagingUserInfo() || { enterprise: {} }
    const min = userData.enterprise.passwordLengthMin
    const max = userData.enterprise.passwordLengthMax
    let group = []
    opts.forEach(option => {
      if (min && max) {
        opts.forEach(item => {
          if (item.id === 1) {
            item.validate = (value) => {
              const reg = new RegExp(`^.{${min},${max}}$`)
              return reg.test(value)
            }
            item.label = intl.get('login.psdLength', { min, max })
          }
        })
      }
      option.iconType = 'info-circle'
      group.push(option.validate)
    })
    setValidateGroup(group)
    setOptions(opts)
  }

  return (
    <div className="update-psd-dialog">
      <div className="back" onClick={handleCancel}>
        <LeftOutlined /><span>{intl.get('login.Back')}</span>
      </div>
      <div className="update-psd-title">
        {intl.get('login.updatePsd')}
      </div>
      <div className="update-psd-subtitle">
        {pwdValidStatus && PwdValidStatusDescription[pwdValidStatus]}
      </div>
      <Form className="update-psd-form" form={form} layout="vertical">
        <Form.Item
          label={intl.get('login.oldPsd')}
          name="oldPsd"
          validateTrigger="onBlur"
          rules={[
            {
              required: true,
              message: intl.get('login.inputOldPsd')
            }
          ]}
        >
          <Input.Password visibilityToggle={false} placeholder={intl.get('login.inputOldPsd')} />
        </Form.Item>
        <Form.Item
          label={intl.get('login.newPsd')}
          name="newPsd"
          validateTrigger="onBlur"
          rules={[
            {
              required: true,
              message: intl.get('login.inputNewPsd')
            },
            {
              validator: validateToNextPassword
            }
          ]}
        >
          <Input.Password
            placeholder={intl.get('login.inputNewPsd')}
            onChange={handlePasswordInput}
            onFocus={() => setPasswordFocused(true)}
            onBlur={() => setPasswordFocused(false)}
          />
        </Form.Item>
        <div className={`update-psd-passwordStrength-wrapper ${passwordFocused ? 'expanded' : ''}`}>
          <PasswordStrength
            passwordLevel={passwordLevel || passwordSecurityLevel.LOW}
            value={newPsd}
            min={passwordLengthMin || 6}
            max={passwordLengthMax || 18}
          />
        </div>
        <Form.Item
          label={intl.get('login.repeatNewPsd')}
          name="rePsd"
          rules={[
            {
              required: true,
              message: intl.get('login.inputNewPsdAgain')
            },
            {
              validator: compareToFirstPassword
            }
          ]}
        >
          <Input.Password
            onPaste={(e) => e.preventDefault()}
            onBlur={handleConfirmBlur}
            placeholder={intl.get('login.inputNewPsdAgain')}
            visibilityToggle={false} />
        </Form.Item>
      </Form>
      <Button
        onClick={onOk}
        type="primary"
        loading={confirmLoading}
        className="btn-submit">{intl.get('login.Confirm')}
      </Button>
    </div>
  )
}

export default UpdatePsd